import React, { createRef, RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { css } from '@emotion/react';
import { Modal } from '@opendoor/bricks/complex';
import { Box, Button, Flex, Icon } from '@opendoor/bricks/core';
import SelectiveSpritesheet from '@opendoor/bricks/core/Icon/SelectiveSpritesheet';
import ChevronLeft16 from '@opendoor/bricks/core/Icon/SpritesheetIcons/ChevronLeft16';
import ChevronRight16 from '@opendoor/bricks/core/Icon/SpritesheetIcons/ChevronRight16';
import { useDevice } from '@opendoor/bricks/hooks/useMedia';
import { RadiiProps } from '@opendoor/bricks/system/theme';
import { breakpoints } from '@opendoor/bricks/theme/eero';

import { generatePresetPictureSet, PresetPictureSet } from 'components/shared/performance/pictures';

import InViewTrackerBox from '../../shared/performance/InViewTracker';
import { numberAsDollar } from '../helpers/format';
import { getFormattedAddresses } from '../helpers/property';
import useTrackProperty from '../helpers/useTrackProperty';
import { AddressDisplayProps } from './AddressDisplay';

const LARGE_HERO_SIZE_CONFIG: PresetPictureSet = [
  {
    minWidth: breakpoints[2],
    maxWidth: breakpoints[3],
    preset: '3:2-md',
    presetScale: '2/3',
  },
  {
    minWidth: breakpoints[3],
    maxWidth: null,
    preset: '3:2-lg',
    presetScale: '2/3',
  },
];

export const SMALL_HERO_SIZE_CONFIG: PresetPictureSet = [
  {
    minWidth: breakpoints[0],
    maxWidth: breakpoints[1],
    preset: '3:2-xs',
  },
  {
    minWidth: breakpoints[1],
    maxWidth: breakpoints[2],
    preset: '3:2-sm',
  },
];

const LARGE_SECONDARY_SIZE_CONFIG: PresetPictureSet = [
  {
    minWidth: breakpoints[2],
    maxWidth: breakpoints[3],
    preset: '3:2-md',
    presetScale: '1/3',
  },
  {
    minWidth: breakpoints[3],
    maxWidth: null,
    preset: '3:2-lg',
    presetScale: '1/3',
  },
];

// Homes Placeholder SVG
export const FALLBACK_URL =
  'https://images.opendoor.com/source/s3/imgdrop-production/ce1501221958405990f1d0d7fe935c51.svg?preset=square-2048';
type CoverProps = {
  photos: Array<string>;
  debugImageSizes?: boolean;
  address: AddressDisplayProps;
  listPrice?: number | null;
};

const LargeScreen: React.FC<CoverProps & { openModal: () => void }> = (props) => {
  const primaryImage = props.photos?.[0] ?? FALLBACK_URL;
  const secondaryImageTop = props.photos?.[1] ?? FALLBACK_URL;
  const secondaryImageBottom = props.photos?.[2] ?? FALLBACK_URL;
  const totalPhotos = props.photos?.length;
  return (
    <Box
      display={['none', null, 'flex']}
      as="ul"
      listStyleType="none"
      margin="0"
      padding="0"
      height={'440px'}
    >
      <Box
        as="li"
        onClick={props.openModal}
        role="button"
        width={['100%', null, '65%']}
        paddingRight={3}
        tabIndex={0}
      >
        {generatePresetPictureSet({
          photoUrl: primaryImage,
          sizes: LARGE_HERO_SIZE_CONFIG,
          debugImageSizes: Boolean(props.debugImageSizes),
          radii: {
            borderTopStartRadius: 'roundedSmooth',
            borderBottomStartRadius: 'roundedSmooth',
            borderTopEndRadius: ['roundedSmooth', null, 'none'],
            borderBottomEndRadius: ['roundedSmooth', null, 'none', 'none'],
          },
          lazy: false,
          preload: true,
        })}
      </Box>
      <Flex height="100%" flexDirection="column" width="35%">
        <Box
          onClick={props.openModal}
          role="button"
          as="li"
          height="50%"
          paddingBottom={2}
          tabIndex={0}
        >
          {generatePresetPictureSet({
            photoUrl: secondaryImageTop,
            sizes: LARGE_SECONDARY_SIZE_CONFIG,
            debugImageSizes: Boolean(props.debugImageSizes),
            radii: {
              borderTopEndRadius: 'roundedSmooth',
            },
            lazy: false,
            preload: true,
          })}
        </Box>
        <Box
          as="li"
          onClick={props.openModal}
          role="button"
          height="50%"
          paddingTop={2}
          position="relative"
          tabIndex={0}
        >
          {totalPhotos > 3 && (
            <Box position="absolute" bottom="0%" right="0%" mb="6" mr="6" maxWidth="75%">
              <Button
                paddingY="2"
                paddingX="5"
                variant="secondary"
                size="sm"
                aria-label={null}
                type="button"
                onClick={props.openModal}
                analyticsName="cosmos-pdp-large-view-all"
              >
                View all {totalPhotos} photos
              </Button>
            </Box>
          )}
          {generatePresetPictureSet({
            photoUrl: secondaryImageBottom,
            sizes: LARGE_SECONDARY_SIZE_CONFIG,
            debugImageSizes: Boolean(props.debugImageSizes),
            radii: {
              borderBottomEndRadius: 'roundedSmooth',
            },
            lazy: false,
            preload: true,
          })}
        </Box>
      </Flex>
    </Box>
  );
};

const isInViewport = (rect: DOMRect | undefined): boolean => {
  if (!rect) {
    return false;
  }
  return (
    rect.left >= 0 && rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};

const SizedPhoto: React.FC<{
  boxRef: React.RefObject<HTMLLIElement & HTMLDivElement>;
  photo: string;
  debugImageSizes?: boolean;
  idx: number;
  totalPhotos: number;
  sizes: PresetPictureSet;
  onPhotoClicked: (slideIndex: number) => void;
  setFirstPhotoVisibility: (inView: boolean) => void;
  setLastPhotoVisibility: (inView: boolean) => void;
  preload?: boolean;
}> = (props) => {
  const photoOnViewChange = React.useCallback((inView: boolean) => {
    if (props.idx === 0) {
      props.setFirstPhotoVisibility(inView);
    }
    if (props.idx === props.totalPhotos - 1) {
      props.setLastPhotoVisibility(inView);
    }
  }, []);
  return (
    <Box
      as="li"
      minWidth="100%"
      scrollSnapAlign="center"
      position="relative"
      onClick={() => {
        props.onPhotoClicked(props.idx);
      }}
      ref={props.boxRef}
      role="button"
      tabIndex={0}
    >
      <InViewTrackerBox
        onViewChange={photoOnViewChange}
        threshold={0.5}
        delay={150}
        initialInView={props.idx === 0 ? true : false}
        skip={props.idx > 0 && props.idx < props.totalPhotos - 1}
        boxProps={{ width: '100%', height: '100%' }}
      >
        {generatePresetPictureSet({
          photoUrl: props.photo,
          sizes: props.sizes,
          debugImageSizes: Boolean(props.debugImageSizes),
          radii: {},
          lazy: props.idx !== 0,
          preload: props.preload,
        })}
      </InViewTrackerBox>
    </Box>
  );
};

const PhotoCarousel: React.FC<
  CoverProps & {
    selectedSlide: number;
    sizes: PresetPictureSet;
    onPhotoClicked: (slideIndex: number) => void;
    carouselRadii: RadiiProps;
    isHero?: boolean;
  }
> = (props) => {
  const photos =
    props.photos.length === 0 ? [FALLBACK_URL] : props.photos.map((x) => x || FALLBACK_URL);
  // Keep all the slide refs in another ref so that
  const slideRefs = useRef<Array<RefObject<HTMLDivElement & HTMLLIElement>>>(
    props.photos.map((_) => createRef<HTMLDivElement & HTMLLIElement>()),
  );
  const [firstPhotoVisible, setFirstPhotoVisible] = useState(true);
  const [lastPhotoVisible, setLastPhotoVisible] = useState(false);
  const goToSlide = useCallback(
    (direction: 'next' | 'previous') => {
      let nextUp: number | undefined;
      const allRefs = slideRefs.current;
      if (!allRefs) {
        return;
      }
      for (let i = 0; i < allRefs.length; i++) {
        const element = allRefs[i].current;
        if (!element) {
          continue;
        }
        // find currently active element
        if (isInViewport(element.getBoundingClientRect())) {
          if (direction === 'next' && i + 1 < allRefs.length) {
            nextUp = i + 1;
          }
          if (direction === 'previous' && i - 1 >= 0) {
            nextUp = i - 1;
          }
          break;
        }
      }
      if (nextUp === undefined) {
        return;
      }
      // scroll the next or previous into view if it exists
      allRefs[nextUp]?.current?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    },
    [slideRefs],
  );
  useEffect(() => {
    slideRefs.current?.[props.selectedSlide]?.current?.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    });
  }, [props.selectedSlide, slideRefs]);
  return (
    <>
      <SelectiveSpritesheet icons={[ChevronLeft16, ChevronRight16]} />
      <Box width="100%" height="100%" position="relative" overscrollBehaviorX="contain">
        <Button
          aria-label="Navigate previous"
          variant="icon"
          position="absolute"
          analyticsName="cosmos-properties-slide-back"
          visibility={firstPhotoVisible ? 'hidden' : 'visible'}
          type="button"
          top="50%"
          left="0%"
          ml="4"
          transform="translate(0%, -50%)"
          background="neutrals0"
          shadow="z1"
          zIndex="1"
          tabIndex={-1}
          onClick={() => {
            goToSlide('previous');
          }}
        >
          <Icon name="chevron-left" color="neutrals100" size={16} />
        </Button>
        <Flex
          flexWrap="nowrap"
          overflowX="auto"
          // when you overscoll the container, don't continue the scroll outside
          // of the container
          overscrollBehaviorX="none"
          overflowY="hidden"
          as="ul"
          listStyleType="none"
          margin="0"
          padding="0"
          minHeight="100%"
          minWidth="100%"
          // Fix for webkit border radius issues
          // https://gist.github.com/ayamflow/b602ab436ac9f05660d9c15190f4fd7b
          css={css`
            -webkit-mask-image: -webkit-radial-gradient(white, black);
          `}
          scrollSnapType="x mandatory"
          flexDirection="row"
          {...props.carouselRadii}
        >
          {photos.map((photo, i) => {
            return (
              <SizedPhoto
                key={i}
                idx={i}
                debugImageSizes={props.debugImageSizes}
                sizes={props.sizes}
                setFirstPhotoVisibility={setFirstPhotoVisible}
                setLastPhotoVisibility={setLastPhotoVisible}
                totalPhotos={photos.length}
                photo={photo}
                onPhotoClicked={props.onPhotoClicked}
                boxRef={slideRefs.current[i]}
                preload={i === 0 && props.isHero}
              />
            );
          })}
        </Flex>
        <Button
          aria-label="Navigate next"
          variant="icon"
          position="absolute"
          analyticsName="cosmos-properties-slide-forward"
          visibility={lastPhotoVisible || photos.length === 1 ? 'hidden' : 'visible'}
          top="50%"
          right="0%"
          type="button"
          mr="4"
          background="neutrals0"
          transform="translate(0%, -50%)"
          shadow="z1"
          tabIndex={-1}
          onClick={() => {
            goToSlide('next');
          }}
        >
          <Icon name="chevron-right" color="neutrals100" size={16} />
        </Button>
      </Box>
    </>
  );
};

export const groupImages = (
  photos: Array<string>,
): Array<Array<{ photoUrl: string; photoIndex: number }>> => {
  const groupedImages = [];
  for (let i = 0; i < photos.length; i++) {
    if (i % 3 === 0) {
      groupedImages.push([{ photoUrl: photos[i], photoIndex: i }]);
    }
    if (i % 3 === 1) {
      groupedImages.push(
        [
          { photoUrl: photos[i], photoIndex: i },
          { photoUrl: photos?.[i + 1], photoIndex: i + 1 },
        ].filter((x) => Boolean(x.photoUrl)),
      );
      i = i + 1;
    }
  }
  return groupedImages;
};

const DESIRED_ASPECT_RATIO = 3 / 2;
const SMALL_LIGHTBOX_IMAGE_RATIO = 0.5;

const pictureImageSetForValues: (
  breakpoints: Array<number>,
  presets: Array<string>,
  presetScale?: Array<string | null>,
) => PresetPictureSet = (breakpoints, presets, presetScale = []) => {
  return breakpoints.map((b, i) => {
    const presetScaleNonNull: string | undefined = presetScale?.[i] ?? undefined;
    return {
      minWidth: b,
      maxWidth: breakpoints?.[i + 1] ?? null,
      preset: presets[i],
      presetScale: presetScaleNonNull,
    };
  });
};

const Lightbox: React.FC<CoverProps> = (props) => {
  const { trackProperty } = useTrackProperty();
  const groupedImages = groupImages(props.photos);
  const responsiveWidthScaleFactors = [100, 100, 35, 25, 25];
  const presets = ['xs', 'sm', 'md', 'lg', 'xl'].map((x) => `3:2-${x}`);
  const widths = responsiveWidthScaleFactors.map((s) => `${s}%`);
  const imageHeightSingle = responsiveWidthScaleFactors.map(
    (s) => `calc(${s}vw * ${1 / DESIRED_ASPECT_RATIO})`,
  );
  const imageHeightDouble = responsiveWidthScaleFactors.map(
    (s) => `calc(${s}vw * ${(1 / DESIRED_ASPECT_RATIO) * SMALL_LIGHTBOX_IMAGE_RATIO})`,
  );
  const pictureSetSingle = pictureImageSetForValues(breakpoints, presets, [
    null,
    null,
    '1/3',
    '1/4',
    '1/4',
  ]);
  const pictureSetDouble = pictureImageSetForValues(breakpoints, presets, [
    '1/2',
    '1/2',
    '1/6',
    '1/8',
    '1/8',
  ]);
  const pictureSetLargeCarousel = pictureImageSetForValues(breakpoints, presets);
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedSlide, setSelectedSlide] = useState(0);
  // useDevice uses legacy breakpoints, however, isMobile maps correctly to the
  // eero breakpoints mobile device sizes
  const { isMobile } = useDevice();
  return (
    <>
      {/* Modal only exists on mobile sizes */}
      <Modal
        analyticsName="cosmos-property-lightbox-mobile-carousel-modal"
        aria-label="Photo viewer carousel"
        showClose={true}
        onRequestClose={() => setModalOpen(false)}
        hasPadding={true}
        mobileSize="sheet"
        isOpen={isMobile && isModalOpen}
      >
        <PhotoCarousel
          {...props}
          onPhotoClicked={(selectedSlide) => {
            trackProperty('property-images', 'mobile-modal-image-click');
            setSelectedSlide(selectedSlide);
          }}
          selectedSlide={selectedSlide}
          sizes={SMALL_HERO_SIZE_CONFIG}
          carouselRadii={{ borderRadius: 'roundedSmooth' }}
        />
      </Modal>
      <Flex height="100%">
        <Box width={widths} overflowY="auto">
          {groupedImages.map((images, i) => {
            return (
              <Flex
                flexDirection={'row'}
                key={i}
                mb="3"
                gap="3"
                width={'100%'}
                overflow="hidden"
                height={images.length === 2 ? imageHeightDouble : imageHeightSingle}
              >
                {images.map((image, j) => {
                  const onClick = () => {
                    trackProperty('property-images', 'select-slide-image');
                    setModalOpen(true);
                    setSelectedSlide(image.photoIndex);
                  };
                  return (
                    <Box
                      key={`${i}-${j}`}
                      role="button"
                      onKeyDown={(e) => {
                        const keyD = e.key !== undefined ? e.key : e.keyCode;
                        if (keyD === 'Enter' || keyD === 13) {
                          onClick();
                        }
                      }}
                      tabIndex={0}
                      onClick={onClick}
                    >
                      {generatePresetPictureSet({
                        photoUrl: image.photoUrl,
                        sizes: images.length === 2 ? pictureSetDouble : pictureSetSingle,
                        debugImageSizes: Boolean(props.debugImageSizes),
                        radii: {},
                        lazy: true,
                      })}
                    </Box>
                  );
                })}
              </Flex>
            );
          })}
        </Box>
        <Flex
          display={['none', null, 'flex']}
          width="100%"
          justifyContent="center"
          alignItems="center"
          maxWidth="1440px"
          mx="auto"
        >
          <Box width={'100%'} paddingX="8">
            <PhotoCarousel
              {...props}
              onPhotoClicked={(selectedSlide) => {
                trackProperty('property-images', 'desktop-image-carousel-click');
                setSelectedSlide(selectedSlide);
              }}
              selectedSlide={selectedSlide}
              sizes={pictureSetLargeCarousel}
              carouselRadii={{ borderRadius: 'squared' }}
            />
          </Box>
        </Flex>
      </Flex>
    </>
  );
};

type CoverImagesBaseProps = CoverProps & {
  isLightboxOpen: boolean;
  buttonCtas?: React.ReactNode;
};

const CoverImages = (props: CoverImagesBaseProps) => {
  const [isModalOpen, setIsModalOpen] = useState(props.isLightboxOpen);
  const { trackProperty } = useTrackProperty();
  const openModal = useCallback(() => {
    setIsModalOpen(true);
    trackProperty('property-images', 'open-modal');
  }, [setIsModalOpen]);
  const closeModal = useCallback(() => setIsModalOpen(false), [setIsModalOpen]);
  const { isMobile } = useDevice();
  const formattedListPrice =
    typeof props.listPrice === 'number' ? numberAsDollar(props.listPrice / 100) : null;
  const { short: shortAddress, full: fullAddress } = getFormattedAddresses(props.address);
  return (
    <>
      <Modal
        analyticsName="cosmos-property-lightbox-modal"
        showClose={true}
        onRequestClose={closeModal}
        headerProps={{
          title: isMobile ? shortAddress : fullAddress,
          leftComponent: <Flex gap="2">{props.buttonCtas}</Flex>,
          subtitle: formattedListPrice || undefined,
          subtitleProps: {
            marginTop: '0',
          },
          titleProps: {
            marginBottom: '0',
            overflowWrap: 'break-word',
          },
        }}
        hasPadding={true}
        desktopAndTabletSize="fullScreen"
        mobileSize="fullScreen"
        isOpen={isModalOpen}
      >
        <Lightbox {...props} />
      </Modal>
      <Box width="100%" height="100%">
        <LargeScreen {...props} openModal={openModal} />
        <Box display={['flex', null, 'none']}>
          <PhotoCarousel
            {...props}
            onPhotoClicked={() => {
              openModal();
            }}
            selectedSlide={0}
            sizes={SMALL_HERO_SIZE_CONFIG}
            carouselRadii={{ borderRadius: 'none' }}
            isHero={true}
          />
        </Box>
      </Box>
    </>
  );
};

export default CoverImages;
